/** System OPORD sitrep template id (see system-templates.json). */
export const OPORD_TEMPLATE_ID = '22222222-2222-2222-2222-222222222222';

export const OPORD_ANALYTICS_SECTION_TEMPLATE_ID =
  'b5c90000-0000-0000-0000-000000000000';

/** OPORD Analytics: the analytics template, or a layer-stats automation. */
export function isAnalyticsSection(
  section:
    | {
        section_template_id?: string | null;
        automation?: { provider?: string } | null;
        output_format_structured?: {
          automation?: { provider?: string } | null;
        } | null;
      }
    | null
    | undefined
): boolean {
  if (!section) return false;
  if (section.section_template_id === OPORD_ANALYTICS_SECTION_TEMPLATE_ID) {
    return true;
  }
  const provider =
    section.automation?.provider ??
    section.output_format_structured?.automation?.provider;
  return provider === 'layerStats';
}

export const OPORD_SITUATION_SECTION_TEMPLATE_ID =
  'b5c90001-0000-0000-0000-000000000001';

export const OPORD_EXECUTION_SECTION_TEMPLATE_ID =
  'b5c90003-0000-0000-0000-000000000003';

export const OPORD_CONCEPT_OF_OPERATIONS_SECTION_TEMPLATE_ID =
  'b5c90031-0000-0000-0000-000000000031';

export const OPORD_FRIENDLY_FORCES_SECTION_TEMPLATE_ID =
  'b5c90013-0000-0000-0000-000000000013';

export function isOpordExecutionParentSection(params: {
  sectionName?: string | null;
  sectionTemplateId?: string | null;
  parentSectionId?: string | null;
}): boolean {
  if (params.parentSectionId) return false;
  if (params.sectionTemplateId === OPORD_EXECUTION_SECTION_TEMPLATE_ID) {
    return true;
  }
  return (params.sectionName ?? '').trim().toLowerCase() === 'execution';
}

export const OPORD_TASKS_SECTION_TEMPLATE_ID =
  'b5c90006-0000-0000-0000-000000000006';

export const DEFAULT_OPORD_TASKS_ACTION_COUNT = 5;
export const MIN_OPORD_TASKS_ACTION_COUNT = 1;
export const MAX_OPORD_TASKS_ACTION_COUNT = 25;

export function clampOpordTasksActionCount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_OPORD_TASKS_ACTION_COUNT;
  return Math.min(
    MAX_OPORD_TASKS_ACTION_COUNT,
    Math.max(MIN_OPORD_TASKS_ACTION_COUNT, Math.round(n))
  );
}

export const GENERIC_AI_INSTRUCTIONS = 'Summarize the information provided';

export const OPORD_FRIENDLY_FORCES_AI_INSTRUCTIONS = `Format as TipTap HTML only (<p>, <ul><li>). Do not use markdown. Cover Higher HQ mission and intent; adjacent and supporting units or agencies when known from DATA.
Also list each incident resource from DATA.FriendlyForcesResources as an <li> that starts with that resource's mentionHtml copied VERBATIM, then 1–2 sentences on how it could be leveraged given the operational environment and threat/hazard conditions. If FriendlyForcesResources is empty or missing, state that no incident resources are available and do not invent @ mentions.`;

/**
 * @deprecated Legacy monolithic Situation instructions — kept for settings
 *   fallback.
 */
export const OPORD_SITUATION_AI_INSTRUCTIONS = `Format the Situation section as HTML only (TipTap). Prefer editing individual Situation subsections rather than the parent shell.`;

export const OPORD_TASKS_AI_INSTRUCTIONS = `Propose concrete subordinate tasks as Planned Actions for this OPORD.

Output MUST be a single fenced JSON code block (\`\`\`json ... \`\`\`) with this exact shape:
{
  "introHtml": "<p>Short TipTap HTML intro summarizing the tasking.</p>",
  "actions": [
    { "name": "Full task text (what to do and why)", "description": "", "checklist": [] }
  ]
}

Rules:
- Put the entire Task in "name". Always set "description" to "".
- Use DATA (Incident ICS, AOR context, Mission/Execution) to propose actionable Planned tasks.
- Do NOT invent actions whose names already appear in DATA.ExistingIncidentActions or DATA.ExistingOrgActions (case-insensitive).
- Propose exactly the number of actions requested in OUTPUT FORMAT / action_count (default ${DEFAULT_OPORD_TASKS_ACTION_COUNT}).
- Prefer short, concrete field tasks a National Guard unit would execute during flood DSCA (e.g. debris removal, move/emplace sandbags, high-water vehicle support, road clearance, sandbag cache resupply, evacuation support at shelters).
- When DATA indicates flooding or a Tennessee flood context, bias toward those mission types; otherwise propose similarly concrete field tasks appropriate to the hazard in DATA.
- Keep names action-oriented and concise; avoid abstract staff/planning language (e.g. "coordinate interagency sync", "monitor situation").
- introHtml must be HTML only (no markdown).
- Outside the JSON fence, do not add other content.`;

const OPORD_SITUATION_OUTPUT_MARKER = 'Area of Operations / Area of Interest';
const OPORD_TASKS_OUTPUT_MARKER = 'Generate to draft Planned actions';

export function isOpordSituationSection(params: {
  sectionName?: string | null;
  outputFormat?: string | null;
  sitrepTemplateId?: string | null;
  sectionTemplateId?: string | null;
}): boolean {
  if (params.sectionTemplateId === OPORD_SITUATION_SECTION_TEMPLATE_ID) {
    return true;
  }
  const name = (params.sectionName ?? '').trim().toLowerCase();
  if (name !== 'situation') return false;

  if (params.sitrepTemplateId === OPORD_TEMPLATE_ID) return true;

  const outputFormat = params.outputFormat ?? '';
  return outputFormat.includes(OPORD_SITUATION_OUTPUT_MARKER);
}

export function isOpordFriendlyForcesSection(params: {
  sectionName?: string | null;
  sectionTemplateId?: string | null;
}): boolean {
  if (params.sectionTemplateId === OPORD_FRIENDLY_FORCES_SECTION_TEMPLATE_ID) {
    return true;
  }
  const name = (params.sectionName ?? '').trim().toLowerCase();
  // Matches "Friendly Forces" and legacy "c. Friendly Forces".
  return name === 'friendly forces' || name.endsWith('friendly forces');
}

export function isOpordTasksSection(params: {
  sectionName?: string | null;
  outputFormat?: string | null;
  sitrepTemplateId?: string | null;
  sectionTemplateId?: string | null;
}): boolean {
  const name = (params.sectionName ?? '').trim().toLowerCase();
  if (name !== 'tasks') return false;

  if (params.sectionTemplateId === OPORD_TASKS_SECTION_TEMPLATE_ID) return true;
  if (params.sitrepTemplateId === OPORD_TEMPLATE_ID) return true;

  const outputFormat = params.outputFormat ?? '';
  return outputFormat.includes(OPORD_TASKS_OUTPUT_MARKER);
}

/** Default AI instructions when toggling a section to AI Summarized. */
export function getDefaultAiInstructionsForSection(params: {
  sectionName?: string | null;
  outputFormat?: string | null;
  sitrepTemplateId?: string | null;
  sectionTemplateId?: string | null;
}): string {
  if (isOpordFriendlyForcesSection(params)) {
    return OPORD_FRIENDLY_FORCES_AI_INSTRUCTIONS;
  }
  if (isOpordSituationSection(params)) {
    return OPORD_SITUATION_AI_INSTRUCTIONS;
  }
  if (isOpordTasksSection(params)) {
    return OPORD_TASKS_AI_INSTRUCTIONS;
  }
  return GENERIC_AI_INSTRUCTIONS;
}
